import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { FileModal } from '../components/ComplianceModals';
import ImportModal from '../components/ImportModal';
import EmptyState from '../components/EmptyState';
import { IconReceipt, IconTrendingUp, IconWallet } from '../components/icons';
import { useCompany } from '../context/CompanyContext';
import { fyLabel } from '../utils/fy';
import api from '../api';
import './Dashboard.css';
import './Calendar.css';
import './DataEntry.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function inr(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  return Math.round((due - today) / 86400000);
}

function statusPill(item) {
  if (!item) return <span className="dash-pill dash-pill-blue">—</span>;
  if (item.is_applicable === false) return <span className="dash-pill dash-pill-blue">N/A</span>;
  if (item.status === 'filed') return <span className="dash-pill dash-pill-green">Filed ✓</span>;
  if (item.status === 'overdue') return <span className="dash-pill dash-pill-red">Overdue</span>;
  if (item.status === 'in_progress') return <span className="dash-pill dash-pill-inprogress">In Progress</span>;
  if (item.status === 'pending') return <span className="dash-pill dash-pill-amber">Pending</span>;
  return <span className="dash-pill dash-pill-blue">—</span>;
}

function GstReturns() {
  const { selectedCompanyId, selectedCompany, fyStartYear } = useCompany();
  const isQuarterly = selectedCompany?.gst_filing_frequency === 'Quarterly';
  const today = new Date();

  const [activeTab, setActiveTab] = useState('monthly');
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());

  const [items, setItems] = useState([]);
  const [monthData, setMonthData] = useState(null);
  const [ytdData, setYtdData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fileModalItem, setFileModalItem] = useState(null);
  const [filing, setFiling] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const loadItems = () => {
    if (!selectedCompanyId) return undefined;
    return api
      .get(`/compliance-items/${selectedCompanyId}`, { params: { fy_year: fyStartYear, category: 'GST' } })
      .then((res) => setItems(res.data))
      .catch((err) => setError(err.message));
  };

  // FY-scoped GST compliance items (for GSTR-3B/GSTR-1 status pills + filing history)
  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, fyStartYear]);

  const confirmFile = (payload) => {
    if (!fileModalItem) return undefined;
    setFiling(true);
    return api
      .post(`/compliance/${fileModalItem.id}/file`, payload)
      .then(() => {
        setFileModalItem(null);
        loadItems();
      })
      .finally(() => setFiling(false));
  };

  const clearFile = () => {
    if (!fileModalItem) return undefined;
    setFiling(true);
    return api
      .post(`/compliance/${fileModalItem.id}/unfile`, {})
      .then(() => {
        setFileModalItem(null);
        loadItems();
      })
      .finally(() => setFiling(false));
  };

  // Monthly tab data
  useEffect(() => {
    if (!selectedCompanyId || activeTab !== 'monthly') return;
    setLoading(true);
    api
      .get(`/gst/${selectedCompanyId}`, { params: { month, year } })
      .then((res) => {
        setMonthData(res.data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedCompanyId, month, year, activeTab]);

  // YTD tab data
  useEffect(() => {
    if (!selectedCompanyId || activeTab !== 'ytd') return;
    setLoading(true);
    api
      .get(`/gst/${selectedCompanyId}/ytd`, { params: { fy_year: fyStartYear } })
      .then((res) => {
        setYtdData(res.data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedCompanyId, fyStartYear, activeTab]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) { m = 1; y += 1; }
    else if (m < 1) { m = 12; y -= 1; }
    setMonth(m);
    setYear(y);
  };

  const gstr3b = items.find((it) => it.form_name === 'GSTR-3B' && it.period_month === month && it.period_year === year);
  const gstr1 = items.find((it) => it.form_name === 'GSTR-1' && it.period_month === month && it.period_year === year);

  const quarterlyReturns = items
    .filter((it) => it.form_name === 'GSTR-1 (Quarterly)' || it.form_name === 'GSTR-3B (Quarterly)')
    .sort((a, b) => (a.due_date < b.due_date ? -1 : 1));
  const iffItems = items
    .filter((it) => it.form_name === 'IFF')
    .sort((a, b) => (a.due_date < b.due_date ? -1 : 1));

  const record = monthData?.data;
  const totalLiability = record ? record.igst_sales + record.cgst_sales + record.sgst_sales : 0;
  const itcAvailable = record ? record.total_itc : 0;
  const netPayable = record ? record.net_payable : 0;

  const filingHistory = items
    .filter((it) => it.status === 'filed')
    .sort((a, b) => (a.due_date < b.due_date ? 1 : -1));

  const gstr3bByPeriod = Object.fromEntries(
    items.filter((it) => it.form_name === 'GSTR-3B').map((it) => [`${it.period_month}-${it.period_year}`, it])
  );

  return (
    <PageLayout>
      <div className="page-title-row">
        <div>
          <h1 className="page-title">GST Returns</h1>
          <p className="page-subtitle">Auto-populated from accounting entries — {fyLabel(fyStartYear)}</p>
        </div>
        <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
          Import Filings
        </button>
      </div>

      {error && <p className="dash-error">Backend unreachable: {error}</p>}

      <div className="de-tabs">
        <button
          type="button"
          className={`de-tab ${activeTab === 'monthly' ? 'active' : ''}`}
          onClick={() => setActiveTab('monthly')}
        >
          Monthly
        </button>
        <button
          type="button"
          className={`de-tab ${activeTab === 'ytd' ? 'active' : ''}`}
          onClick={() => setActiveTab('ytd')}
        >
          YTD
        </button>
      </div>

      <div className={loading ? 'dash-refreshing' : ''}>
        {activeTab === 'monthly' && (
          <>
            <div className="cal-month-nav" style={{ display: 'inline-flex', marginBottom: 20 }}>
              <button className="cal-nav-btn" type="button" onClick={() => changeMonth(-1)}>&larr;</button>
              <span className="cal-month-label">{MONTH_NAMES[month - 1]} {year}</span>
              <button className="cal-nav-btn" type="button" onClick={() => changeMonth(1)}>&rarr;</button>
            </div>

            <div className="dash-stat-grid dash-stat-grid-3">
              <div className="dash-stat-card">
                <div className="dash-stat-icon orange"><IconReceipt /></div>
                <div className="dash-stat-value dash-serif">{inr(totalLiability)}</div>
                <div className="dash-stat-label">Total Tax Liability</div>
              </div>
              <div className="dash-stat-card">
                <div className="dash-stat-icon green"><IconTrendingUp /></div>
                <div className="dash-stat-value dash-serif green">{inr(itcAvailable)}</div>
                <div className="dash-stat-label">ITC Available</div>
              </div>
              <div className="dash-stat-card">
                <div className={`dash-stat-icon ${netPayable > 0 ? 'red' : 'grey'}`}><IconWallet /></div>
                <div className={`dash-stat-value dash-serif ${netPayable > 0 ? 'red' : 'grey'}`}>{inr(netPayable)}</div>
                <div className="dash-stat-label">Net Payable</div>
              </div>
            </div>

            {!isQuarterly && (
              <div className="dash-card">
                <div className="dash-card-title-row">
                  <h2 className="dash-card-title">GSTR-3B — {MONTH_NAMES[month - 1]} {year}</h2>
                  {gstr3b && statusPill(gstr3b)}
                </div>

                {record ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Nature</th>
                        <th>Taxable Value</th>
                        <th>IGST</th>
                        <th>CGST</th>
                        <th>SGST</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Outward taxable supplies</td>
                        <td className="dash-serif">{inr(record.total_sales)}</td>
                        <td className="dash-serif">{inr(record.igst_sales)}</td>
                        <td className="dash-serif">{inr(record.cgst_sales)}</td>
                        <td className="dash-serif">{inr(record.sgst_sales)}</td>
                        <td className="dash-serif">{inr(totalLiability)}</td>
                      </tr>
                      <tr>
                        <td>Less: Input Tax Credit</td>
                        <td className="dash-serif">—</td>
                        <td className="dash-serif">−{inr(record.igst_itc)}</td>
                        <td className="dash-serif">−{inr(record.cgst_itc)}</td>
                        <td className="dash-serif">−{inr(record.sgst_itc)}</td>
                        <td className="dash-serif">−{inr(record.total_itc)}</td>
                      </tr>
                      <tr className="dash-table-total">
                        <td>Net Payable</td>
                        <td className="dash-serif">—</td>
                        <td className="dash-serif">—</td>
                        <td className="dash-serif">—</td>
                        <td className="dash-serif">—</td>
                        <td className="dash-serif">{inr(netPayable)}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <EmptyState
                    icon={IconReceipt}
                    message={`No GST data entered for ${MONTH_NAMES[month - 1]} ${year}.`}
                    ctaLabel="Go to Data Entry"
                    to="/data-entry"
                  />
                )}

                <div className="dash-btn-row">
                  {gstr3b && gstr3b.is_applicable !== false && (
                    <button
                      className="dash-btn dash-btn-orange"
                      type="button"
                      onClick={() => setFileModalItem(gstr3b)}
                    >
                      {gstr3b.status === 'filed' ? 'Edit Filing Date' : 'Mark GSTR-3B as Filed'}
                    </button>
                  )}
                  <button className="dash-btn dash-btn-outline" type="button">Download JSON</button>
                  <button className="dash-btn dash-btn-outline" type="button">Ask AI ↗</button>
                </div>
              </div>
            )}

            {!isQuarterly && (
              <div className="dash-card">
                <h2 className="dash-card-title">GSTR-1 — Outward Supplies</h2>
                {gstr1 ? (
                  <p className="dash-table-sub" style={{ marginBottom: 16 }}>
                    Due {formatDate(gstr1.due_date)} —{' '}
                    {gstr1.is_applicable === false
                      ? 'marked Not Applicable'
                      : gstr1.status === 'filed'
                      ? `filed on ${formatDate(gstr1.date_of_filing)}`
                      : daysUntil(gstr1.due_date) < 0
                      ? `${Math.abs(daysUntil(gstr1.due_date))} days overdue`
                      : `${daysUntil(gstr1.due_date)} days remaining`}
                  </p>
                ) : (
                  <p className="dash-table-sub" style={{ marginBottom: 16 }}>No GSTR-1 item found for this month.</p>
                )}
                <div className="dash-btn-row">
                  <button className="dash-btn dash-btn-outline" type="button">Prepare GSTR-1</button>
                  {gstr1 && gstr1.is_applicable !== false && (
                    <button
                      className="dash-btn dash-btn-orange"
                      type="button"
                      onClick={() => setFileModalItem(gstr1)}
                    >
                      {gstr1.status === 'filed' ? 'Edit Filing Date' : 'Mark GSTR-1 as Filed'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {isQuarterly && (
              <div className="dash-card">
                <h2 className="dash-card-title">Quarterly GST Filings (QRMP) — {fyLabel(fyStartYear)}</h2>
                <p className="dash-table-sub" style={{ marginBottom: 16 }}>
                  GSTR-1 and GSTR-3B file once per quarter for this company — the monthly figures above
                  are your accounting entries, not separate filings.
                </p>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Form</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Penalty/day</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quarterlyReturns.length === 0 && (
                      <tr><td colSpan={5} className="dash-empty">No quarterly GST filings for this FY.</td></tr>
                    )}
                    {quarterlyReturns.map((it) => (
                      <tr key={it.id} style={it.is_applicable === false ? { opacity: 0.55 } : undefined}>
                        <td>{it.form_name}</td>
                        <td>{formatDate(it.due_date)}</td>
                        <td>{statusPill(it)}</td>
                        <td className="dash-serif">{inr(it.penalty_per_day)}</td>
                        <td>
                          {it.is_applicable !== false && (
                            <button className="dash-btn dash-btn-orange" type="button" onClick={() => setFileModalItem(it)}>
                              {it.status === 'filed' ? 'Edit Filing Date' : 'Mark as Filed'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {isQuarterly && (
              <div className="dash-card">
                <h2 className="dash-card-title">IFF (Optional) — {fyLabel(fyStartYear)}</h2>
                <p className="dash-table-sub" style={{ marginBottom: 16 }}>
                  Invoice Furnishing Facility for months 1-2 of each quarter — optional, no late fee.
                </p>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {iffItems.length === 0 && (
                      <tr><td colSpan={4} className="dash-empty">No IFF items for this FY.</td></tr>
                    )}
                    {iffItems.map((it) => (
                      <tr key={it.id} style={it.is_applicable === false ? { opacity: 0.55 } : undefined}>
                        <td>{it.period_month}/{it.period_year}</td>
                        <td>{formatDate(it.due_date)}</td>
                        <td>{statusPill(it)}</td>
                        <td>
                          {it.is_applicable !== false && (
                            <button className="dash-btn cal-btn-grey" type="button" onClick={() => setFileModalItem(it)}>
                              {it.status === 'filed' ? 'Edit Filing Date' : 'Mark as Filed'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'ytd' && ytdData && (
          <>
            <div className="dash-stat-grid dash-stat-grid-3">
              <div className="dash-stat-card">
                <div className="dash-stat-icon orange"><IconTrendingUp /></div>
                <div className="dash-stat-value dash-serif">{inr(ytdData.total_sales)}</div>
                <div className="dash-stat-label">Total Sales (Apr–{MONTH_NAMES[month - 1].slice(0, 3)})</div>
              </div>
              <div className="dash-stat-card">
                <div className="dash-stat-icon green"><IconTrendingUp /></div>
                <div className="dash-stat-value dash-serif green">{inr(ytdData.total_itc)}</div>
                <div className="dash-stat-label">Total ITC</div>
              </div>
              <div className="dash-stat-card">
                <div className={`dash-stat-icon ${ytdData.total_net_payable > 0 ? 'red' : 'grey'}`}><IconWallet /></div>
                <div className={`dash-stat-value dash-serif ${ytdData.total_net_payable > 0 ? 'red' : 'grey'}`}>
                  {inr(ytdData.total_net_payable)}
                </div>
                <div className="dash-stat-label">Total GST Paid</div>
              </div>
            </div>

            <div className="dash-card">
              <h2 className="dash-card-title">Month-wise — {fyLabel(fyStartYear)}</h2>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Sales</th>
                    <th>GST Liability</th>
                    <th>ITC</th>
                    <th>Net GST</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ytdData.months.length === 0 && (
                    <tr><td colSpan={6} className="dash-empty">No GST data entered yet this FY.</td></tr>
                  )}
                  {ytdData.months.map((m) => {
                    const compliance = gstr3bByPeriod[`${m.month}-${m.year}`];
                    const liability = m.igst_sales + m.cgst_sales + m.sgst_sales;
                    return (
                      <tr key={m.id}>
                        <td>{MONTH_NAMES[m.month - 1]} {m.year}</td>
                        <td className="dash-serif">{inr(m.total_sales)}</td>
                        <td className="dash-serif">{inr(liability)}</td>
                        <td className="dash-serif">{inr(m.total_itc)}</td>
                        <td className="dash-serif">{inr(m.net_payable)}</td>
                        <td>{compliance ? statusPill(compliance) : '—'}</td>
                      </tr>
                    );
                  })}
                  {ytdData.months.length > 0 && (
                    <tr className="dash-table-total">
                      <td>Total</td>
                      <td className="dash-serif">{inr(ytdData.total_sales)}</td>
                      <td className="dash-serif">
                        {inr(ytdData.months.reduce((sum, m) => sum + m.igst_sales + m.cgst_sales + m.sgst_sales, 0))}
                      </td>
                      <td className="dash-serif">{inr(ytdData.total_itc)}</td>
                      <td className="dash-serif">{inr(ytdData.total_net_payable)}</td>
                      <td>—</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="dash-card">
          <h2 className="dash-card-title">Filing History — {fyLabel(fyStartYear)}</h2>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Form</th>
                <th>Filed On</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filingHistory.length === 0 && (
                <tr><td colSpan={5} className="dash-empty">No filed GST returns yet this FY.</td></tr>
              )}
              {filingHistory.map((it) => (
                <tr key={it.id}>
                  <td>{it.period_month}/{it.period_year}</td>
                  <td>{it.form_name}</td>
                  <td>{formatDate(it.date_of_filing)}</td>
                  <td><span className="dash-pill dash-pill-green">Filed ✓</span></td>
                  <td>
                    <button className="dash-btn cal-btn-grey" type="button" onClick={() => setFileModalItem(it)}>
                      Edit Filing Date
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FileModal
        item={fileModalItem}
        onClose={() => setFileModalItem(null)}
        onConfirm={confirmFile}
        onClear={clearFile}
        busy={filing}
      />

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="gst"
        moduleLabel="GST Returns"
        companyId={selectedCompanyId}
        onImported={loadItems}
      />
    </PageLayout>
  );
}

export default GstReturns;
